import React, { useEffect, useRef, useState } from 'react';
import LayersIcon from '@mui/icons-material/Layers';
import { MAP_LAYER_OPTIONS, ActiveOptionCheck } from './MapLayerOptions';
import {
  mapControlsPanelBackground,
  mapControlsPanelRight,
  mapControlsPanelMaxWidth,
  mapControlsLayerPanelTop,
  mapControlsLayerPanelBottom,
} from '../MapControls/MapControls.styles';
import MapToolPanelHeader from '../MapControls/MapToolPanelHeader';
import { probeIgnBasemaps } from '../../utils/ignBasemapProbe';

const LayerSelectorPanel = ({
  currentView,
  onSelect,
  onClose,
  isLoggedIn,
  disabledMessage,
  topOffset = 0,
  bottomOffset = 0,
  anchor = 'top',
}) => {
  const panelRef = useRef(null);
  const [ignAvailable, setIgnAvailable] = useState({ street: true, streetDark: true });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    probeIgnBasemaps().then((result) => {
      if (!cancelled) setIgnAvailable(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      ref={panelRef}
      style={{
        position: 'absolute',
        right: mapControlsPanelRight,
        ...(anchor === 'bottom'
          ? { bottom: mapControlsLayerPanelBottom(bottomOffset) }
          : { top: mapControlsLayerPanelTop(topOffset) }),
        zIndex: 1100,
        backgroundColor: mapControlsPanelBackground,
        borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.1)',
        padding: '12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        minWidth: anchor === 'bottom' ? 0 : '280px',
        maxWidth: mapControlsPanelMaxWidth,
        width: anchor === 'bottom' ? 'min(280px, 100%)' : undefined,
        boxSizing: 'border-box',
        animation: 'layerPanelIn 0.15s ease-out',
      }}
    >
      <MapToolPanelHeader
        icon={LayersIcon}
        title="Vista del mapa"
        onClose={onClose}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {MAP_LAYER_OPTIONS.map((option) => {
          const isActive = currentView === option.id;
          const ignDown = Boolean(option.requiresIgn && ignAvailable[option.id] === false);
          const needsLogin = !isLoggedIn && !isActive;
          const isDisabled = needsLogin || ignDown;
          const sublabel = needsLogin
            ? 'Inicia sesión para cambiar vista de mapa'
            : ignDown
              ? 'No disponible ahora'
              : option.sublabel;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => {
                if (isDisabled) return;
                onSelect(option.id);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '7px 8px',
                borderRadius: '8px',
                border: isActive
                  ? '1px solid rgba(255,255,255,0.25)'
                  : '1px solid transparent',
                backgroundColor: isActive
                  ? 'rgba(255,255,255,0.1)'
                  : 'transparent',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.12s, border-color 0.12s',
                textAlign: 'left',
                width: '100%',
                opacity: isDisabled && !isActive ? 0.45 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isActive && !isDisabled) {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive && !isDisabled) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <div style={{
                borderRadius: '5px',
                overflow: 'hidden',
                flexShrink: 0,
                border: isActive
                  ? '1.5px solid rgba(255,255,255,0.4)'
                  : '1.5px solid rgba(255,255,255,0.12)',
              }}>
                <option.Thumbnail />
              </div>

              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: isActive ? '600' : '400',
                  color: isActive ? '#ffffff' : 'rgba(255,255,255,0.75)',
                  lineHeight: 1.2,
                }}>
                  {option.label}
                </div>
                <div style={{
                  fontSize: '10px',
                  color: isDisabled
                    ? 'rgba(255,255,255,0.28)'
                    : 'rgba(255,255,255,0.35)',
                  marginTop: '2px',
                }}>
                  {sublabel}
                </div>
              </div>

              {isActive && <ActiveOptionCheck />}
            </button>
          );
        })}
      </div>

      <style>{`
        @keyframes layerPanelIn {
          from { opacity: 0; transform: translateX(6px) scale(0.97); }
          to   { opacity: 1; transform: translateX(0)  scale(1);    }
        }
      `}</style>
    </div>
  );
};

export default LayerSelectorPanel;
